import React, { useState } from 'react'
import { CalendarGridController } from '../../hooks'
import { DAY_CELL_W, HEADER_H } from '../../constants'
import { Container } from '../../../Container'
import { usePopover } from '../../../../hooks/usePopover'
import { HeaderPopover } from './HeaderPopover'
import { DateTime } from 'luxon'
import { useTimezone } from '../../../../contexts/timezone'
import { Theme } from '@material-ui/core'
import { ezScheduleDetails } from '../../../../models/WorkOrder/types'
import { UpdateEzScheduleParams } from '../../../../hooks/useEzSchedule/types'
import { typographyStyle } from '../../../../theme/typographyStyle'

interface Props {
    theme: Theme
    startDate: DateTime
    endDate: DateTime
    calendarGridState: CalendarGridController
    ezSchedule: ezScheduleDetails
    updateEzSchedule: (params: UpdateEzScheduleParams) => void
    handleChooseStartTransferDate: (clickX: number) => void
    handleChooseEndTransferDate: (clickX: number) => void
    handleChooseDestUnitReadyDate: (clickX: number) => void
    handleChooseDtUnitReadyDate: (clickX: number) => void
    selectedPhaseId: number
}

export const CalendarGrid = (props: Props) => {
    const {
        calendarGridState,
        theme,
        startDate: scheduleStartDate,
        endDate: scheduleEndDate,
        ezSchedule,
        updateEzSchedule,
        selectedPhaseId,
    } = props

    if (ezSchedule === null) {
        return null
    }

    const {
        convertGridPositionToDate,
        getClickedGridPosition,
        gridPositionInfo,
        getDateGridPosition,
        gridRef,
    } = calendarGridState

    const { currentTimezone } = useTimezone()

    const today = DateTime.now().setZone(currentTimezone).startOf('day')
    const todayMs = today.toMillis()

    const schStartMs = scheduleStartDate.startOf('day').toMillis()
    const schEndMs = scheduleEndDate.startOf('day').toMillis()

    const popoverState = usePopover()
    const [selectedDate, setSelecteDate] = useState(today)

    const dateLabels: JSX.Element[] = []
    const grid: JSX.Element[] = []
    const dayOffColumns: JSX.Element[] = []

    const daysOff =
        ezSchedule?.phases.find((phase) => phase.phase.id === selectedPhaseId)
            ?.day_off_list ?? []

    daysOff.forEach((dayOff) => {
        if (dayOff.schedule === null) return

        const dayOffDate = DateTime.fromISO(dayOff.date)
            .setZone(currentTimezone)
            .startOf('day')

        dayOffColumns.push(
            <div
                key={`DAY-OFF-COL-${dayOff.id}-${dayOffDate.toMillis()}`}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: getDateGridPosition(dayOffDate) * DAY_CELL_W,
                    bottom: 0,
                    width: DAY_CELL_W,
                    boxSizing: 'border-box',
                    backgroundColor: 'rgba(30, 30, 30, 0.2)',
                    borderRadius: theme.shape.borderRadius,
                    zIndex: theme.zIndex.modal + 1,
                    pointerEvents: 'none',
                }}
            />,
        )
    })

    for (let i = 0; i < gridPositionInfo.numDisplayDays; i++) {
        const currentDate = convertGridPositionToDate(i)

        const startOfCurrentDate = currentDate.startOf('day')
        const currentDayMs = startOfCurrentDate.toMillis()

        const dateLabel = currentDate.toFormat('MM/dd')
        let dayLabel: string | JSX.Element = weekday[currentDate.weekday]
        if (currentDayMs === schStartMs) {
            dayLabel = (
                <span
                    style={{
                        color: '#2e7d32',
                        fontWeight: 700,
                    }}
                >
                    (Start)
                </span>
            )
        } else if (currentDayMs === schEndMs) {
            dayLabel = (
                <span
                    style={{
                        color: '#c62828',
                        fontWeight: 700,
                    }}
                >
                    (End)
                </span>
            )
        }

        if (currentDayMs === todayMs) {
            dayLabel = (
                <div
                    style={{
                        color: 'white',
                        backgroundColor: '#016fb9',
                        border: theme.shape.borderRadius,
                        borderRadius: theme.shape.borderRadius,
                        padding: 2,
                        paddingLeft: 4,
                        paddingRight: 4,
                        fontWeight: 700,
                    }}
                >
                    {dayLabel}
                </div>
            )
        }

        let backgroundColor = 'white'
        if (i % 2 === 0) {
            backgroundColor = theme.palette.grey[50]
        }

        if (currentDayMs < schStartMs || currentDayMs > schEndMs) {
            backgroundColor = 'white'
        }

        if (currentDayMs === schStartMs) {
            backgroundColor = 'rgba(216, 255, 234, 0.9)'
        } else if (currentDayMs === schEndMs) {
            backgroundColor = 'rgba(255, 228, 228, 0.9)'
        }

        dateLabels.push(
            <DateLabel
                key={`${startOfCurrentDate.toMillis()}`}
                {...props}
                dayLabel={dayLabel}
                dateLabel={dateLabel}
                onClick={(e) => {
                    if (gridRef.current === null) return

                    const gridPos = getClickedGridPosition(e.pageX)
                    const clickedDate = convertGridPositionToDate(gridPos)

                    setSelecteDate(clickedDate)
                    popoverState.handleOpen(e)
                }}
            />,
        )

        grid.push(
            <div
                key={`GRID-COL-${i}`}
                style={{
                    cursor: 'pointer',
                    position: 'absolute',
                    top: 0,
                    left: i * DAY_CELL_W,
                    bottom: 0,
                    width: DAY_CELL_W,
                    borderLeft: `1px solid ${theme.palette.grey[200]}`,
                    boxSizing: 'border-box',
                    backgroundColor: backgroundColor,
                }}
            />,
        )
    }

    const selectedPhase = ezSchedule?.phases.find(
        (phase) => phase.phase.id === selectedPhaseId,
    )
    const startTransferDate = selectedPhase?.transfer_start_date
    const endTransferDate = selectedPhase?.transfer_end_date
    const destUnitReadyDate = selectedPhase?.dest_unit_ready_date
    const dtUnitReadyDate = selectedPhase?.dt_unit_ready_date

    const transferDateColumns = buildDateRangeColumns({
        keyPrefix: 'transfer',
        startIso: startTransferDate,
        endIso: endTransferDate,
        currentTimezone,
        getDateGridPosition,
        theme,
        backgroundColor: 'rgba(86, 210, 254, 0.2)',
        border: '3px solid rgba(86, 210, 254)',
    })
    const destReadyColumns = buildDateRangeColumns({
        keyPrefix: 'dest-ready',
        startIso: destUnitReadyDate,
        endIso: destUnitReadyDate,
        currentTimezone,
        getDateGridPosition,
        theme,
        backgroundColor: 'rgba(255, 152, 0, 0.22)',
        border: '3px solid rgba(255, 167, 38, 0.85)',
    })
    const dtReadyColumns = buildDateRangeColumns({
        keyPrefix: 'dt-ready',
        startIso: dtUnitReadyDate,
        endIso: dtUnitReadyDate,
        currentTimezone,
        getDateGridPosition,
        theme,
        backgroundColor: 'rgba(230, 81, 0, 0.18)',
        border: '3px solid rgba(230, 81, 0, 0.75)',
    })

    return (
        <>
            <Container
                style={{
                    flexDirection: 'column',
                    borderBottom: `1px solid ${theme.palette.grey[400]}`,
                    height: HEADER_H,
                    width: 'max-content',
                    padding: 4,
                    position: 'sticky',
                    top: 0,
                    zIndex: theme.zIndex.modal + 3,
                    backgroundColor: theme.palette.background.paper,
                }}
            >
                <Container>{dateLabels}</Container>

                <HeaderPopover
                    {...props}
                    popoverState={popoverState}
                    selectedDate={selectedDate}
                    updateEzSchedule={updateEzSchedule}
                />
            </Container>

            {grid}
            {destReadyColumns}
            {dtReadyColumns}
            {transferDateColumns}
            {dayOffColumns}
        </>
    )
}

const weekday = ['', 'Mon', 'Tue', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun']

interface DateRangeColumnArgs {
    keyPrefix: string
    startIso: string | null | undefined
    endIso: string | null | undefined
    currentTimezone: string
    getDateGridPosition: (date: DateTime) => number
    theme: Theme
    backgroundColor: string
    border: string
}

const buildDateRangeColumns = (args: DateRangeColumnArgs): JSX.Element[] => {
    const {
        keyPrefix,
        startIso,
        endIso,
        currentTimezone,
        getDateGridPosition,
        theme,
        backgroundColor,
        border,
    } = args
    const columns: JSX.Element[] = []
    const barStyle = (left: number, width: number): React.CSSProperties => ({
        position: 'absolute',
        top: 0,
        left,
        bottom: 0,
        width,
        boxSizing: 'border-box',
        backgroundColor,
        border,
        borderRadius: theme.shape.borderRadius,
        zIndex: theme.zIndex.modal + 1,
        pointerEvents: 'none',
    })

    if (startIso && endIso) {
        const startTd = DateTime.fromISO(startIso)
            .setZone(currentTimezone)
            .startOf('day')
        const endTd = DateTime.fromISO(endIso)
            .setZone(currentTimezone)
            .startOf('day')
        const startPosition = getDateGridPosition(startTd)
        const endPosition = getDateGridPosition(endTd)
        columns.push(
            <div
                key={`${keyPrefix}-range`}
                style={barStyle(
                    startPosition * DAY_CELL_W,
                    (endPosition - startPosition + 1) * DAY_CELL_W,
                )}
            />,
        )
        return columns
    }
    if (startIso) {
        const startTd = DateTime.fromISO(startIso)
            .setZone(currentTimezone)
            .startOf('day')
        columns.push(
            <div
                key={`${keyPrefix}-start`}
                style={barStyle(
                    getDateGridPosition(startTd) * DAY_CELL_W,
                    DAY_CELL_W,
                )}
            />,
        )
        return columns
    }
    if (endIso) {
        const endTd = DateTime.fromISO(endIso)
            .setZone(currentTimezone)
            .startOf('day')
        columns.push(
            <div
                key={`${keyPrefix}-end`}
                style={barStyle(
                    getDateGridPosition(endTd) * DAY_CELL_W,
                    DAY_CELL_W,
                )}
            />,
        )
    }
    return columns
}

interface DateLabelProps {
    theme: Theme
    dateLabel: string
    dayLabel: string | JSX.Element
    onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}

const DateLabel = (props: DateLabelProps) => {
    const { theme, dateLabel, dayLabel } = props
    return (
        <Container
            style={{
                flexDirection: 'column',
                minWidth: DAY_CELL_W,
                maxWidth: DAY_CELL_W,
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
            }}
            onClick={props.onClick}
        >
            <span
                style={{
                    ...typographyStyle(theme.typography.subtitle1),
                    fontWeight: 700,
                }}
            >
                {dateLabel}
            </span>
            <span
                style={{
                    ...typographyStyle(theme.typography.body2),
                    fontWeight: 300,
                }}
            >
                {dayLabel}
            </span>
        </Container>
    )
}
